/**
 * One group's own activity screen: its feed, oldest page loaded first, and
 * the CSV export beside it. Both exist so that what an edit or a delete did
 * to the group's money is checkable — one as a sentence with the before/after
 * underneath it (`src/screens/Activity.tsx`'s `ActivityFeed`), the other as a
 * file somebody who cannot see this screen at all can still read
 * (`server/export.ts`).
 *
 * Kept out of `Activity.tsx` on purpose: this screen pulls in
 * `src/queries.ts`, which pulls in `src/queue-document.ts` for its offline
 * write queue, which touches `indexedDB` at module scope — real in a browser
 * and not around in the plain-Node test environment `tests/activity-client.test.ts`
 * runs pure functions like `describeActivity` and `activityDetail` in. That
 * file has no business loading a browser API it never asked for; this one is
 * where that import belongs.
 */

import { groupExportUrl, type GroupActivityResponse } from '../api'
import { useGroup, useGroupActivity } from '../queries'
import type { Route } from '../routes'
import { ActivityFeed } from './Activity'

export function GroupActivity({
  groupId,
  navigate,
}: {
  groupId: string
  navigate: (to: Route) => void
}) {
  const group = useGroup(groupId)
  const activity = useGroupActivity(groupId)

  const pages: GroupActivityResponse[] = activity.data?.pages ?? []
  const entries = pages.flatMap((page) => page.activity)
  const currency = pages[0]?.group.currency ?? group.data?.group.currency ?? 'USD'
  const groupName = pages[0]?.group.name ?? group.data?.group.name ?? 'Group'
  const members = group.data?.members ?? []
  const nameOf = new Map(members.map((member) => [member.userId, member.displayName]))

  return (
    <>
      <p className="crumbs">
        <button
          type="button"
          className="button--quiet"
          onClick={() => navigate({ name: 'group', groupId })}
        >
          ← {groupName}
        </button>
      </p>

      <section className="card">
        <h2>Activity</h2>
        <p className="actions">
          {/*
            A real link with `download`, not a `fetch` — `server/export.ts`
            says why: the file is authorised by the same session cookie this
            request already carries, and there is nothing here worth holding
            in memory as a blob first.
          */}
          <a className="button--quiet" href={groupExportUrl(groupId)} download>
            Export CSV
          </a>
        </p>

        <ActivityFeed
          entries={entries}
          isPending={activity.isPending}
          isError={activity.isError}
          error={activity.error}
          showGroup={false}
          currency={currency}
          nameOf={nameOf}
        />

        {activity.hasNextPage && (
          <p className="actions">
            <button
              type="button"
              className="button--quiet"
              disabled={activity.isFetchingNextPage}
              onClick={() => void activity.fetchNextPage()}
            >
              {activity.isFetchingNextPage ? 'Fetching…' : 'Older activity'}
            </button>
          </p>
        )}
      </section>
    </>
  )
}
